/**
 * engine/silence.ts — a tenth of a second of nothing, as a `data:` address.
 *
 * Task 45. `spec.md` «Микс и дополнения плеера», «Две деки»: the first tap unlocks both decks,
 * and the second one has no track to play yet. It gets this instead: a `play()` inside the tap is
 * what lifts the phone's lock from an element, and the deck is paused again on its first sound
 * (`engine/player.ts`). A file of our own build, not a request: nothing leaves the phone for it,
 * and the page policy lets a `data:` sound through — it declares neither `media-src` nor
 * `default-src` (`apps/api/test/static-cache.test.ts` holds that).
 *
 * WAV, 16-bit, mono, 8 000 a second: the plainest format both WebKit and Chrome play. Whether a
 * phone inside Telegram takes it is **not checked** on a phone; a phone that refuses it leaves the
 * second deck locked, and the player then goes from track to track as before task 45.
 */

/** How long the silence lasts, in samples, and how many a second. */
const SAMPLES = 800;
const RATE = 8_000;
const BYTES_PER_SAMPLE = 2;
const HEADER = 44;

function silentWav(): Uint8Array {
  const dataBytes = SAMPLES * BYTES_PER_SAMPLE;
  const bytes = new Uint8Array(HEADER + dataBytes);
  const view = new DataView(bytes.buffer);
  const ascii = (at: number, text: string): void => {
    for (let i = 0; i < text.length; i += 1) bytes[at + i] = text.charCodeAt(i);
  };
  ascii(0, 'RIFF');
  view.setUint32(4, HEADER - 8 + dataBytes, true);
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  view.setUint32(16, 16, true); // the size of the format block
  view.setUint16(20, 1, true); // plain PCM
  view.setUint16(22, 1, true); // one channel
  view.setUint32(24, RATE, true);
  view.setUint32(28, RATE * BYTES_PER_SAMPLE, true);
  view.setUint16(32, BYTES_PER_SAMPLE, true);
  view.setUint16(34, BYTES_PER_SAMPLE * 8, true);
  ascii(36, 'data');
  view.setUint32(40, dataBytes, true);
  // Signed 16-bit silence is zero, which the array already is.
  return bytes;
}

function base64(bytes: Uint8Array): string {
  let text = '';
  for (const byte of bytes) text += String.fromCharCode(byte);
  return btoa(text);
}

/** The second deck's first sound. */
export const SILENCE_URL = `data:audio/wav;base64,${base64(silentWav())}`;
